import { type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { parseEntryInput } from '../utils/parseEntryInput';
import type { RegNumbersStatus } from '../utils/regNumbersTextTransformer';
import type { ImportEntry } from '../types/importTypes';

interface MultiImportReviewInputProps {
  entries: ImportEntry[];
  status: RegNumbersStatus | null;
  fetching: boolean;
  onDeleteEntry: (regNumber: string) => void;
  onAddEntry: (entry: ImportEntry) => void;
}

function EntryChipLabel({ entry }: { entry: ImportEntry }) {
  if (!entry.operationalRef) {
    return <span>{entry.queryRegNumber}</span>;
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{entry.queryRegNumber}</span>
      <Box
        component="span"
        sx={{
          bgcolor: 'secondary.main',
          color: 'secondary.contrastText',
          fontSize: '0.7rem',
          fontWeight: 600,
          px: 0.75,
          py: 0.125,
          borderRadius: 1,
          lineHeight: 1.4,
        }}
      >
        {entry.operationalRef}
      </Box>
    </Box>
  );
}

export default function MultiImportReviewInput({
  entries,
  status,
  fetching,
  onDeleteEntry,
  onAddEntry,
}: MultiImportReviewInputProps) {
  const { t } = useTranslation();
  const [newEntry, setNewEntry] = useState('');

  const handleAddEntry = () => {
    const parsed = parseEntryInput(newEntry);
    if (parsed) {
      onAddEntry(parsed);
    }
    setNewEntry('');
  };

  const handleEntryKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEntry();
    }
  };

  if (fetching) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography sx={{ mb: 2 }}>
          {t('import.multi.fetching', { count: entries.length })}
        </Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <>
      {status && (
        <Alert severity={status.warnLevel} sx={{ mb: 2 }}>
          {status.message}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: 80,
          maxHeight: 240,
          overflowY: 'auto',
          alignContent: 'flex-start',
        }}
        data-testid="multi-import-tags"
      >
        {entries.map(entry => (
          <Chip
            key={entry.queryRegNumber}
            label={<EntryChipLabel entry={entry} />}
            onDelete={() => onDeleteEntry(entry.queryRegNumber)}
            size="small"
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <TextField
          size="small"
          value={newEntry}
          onChange={e => setNewEntry(e.target.value)}
          onKeyDown={handleEntryKeyDown}
          placeholder={t('import.multi.addPlaceholder')}
          fullWidth
          data-testid="multi-import-add-input"
        />
        <Button
          variant="outlined"
          onClick={handleAddEntry}
          disabled={!newEntry.trim()}
          data-testid="multi-import-add-button"
        >
          {t('import.multi.add')}
        </Button>
      </Box>
    </>
  );
}
